/** Fotos: verkleinern, hochladen, mit Token laden (und im Speicher halten). */
import { MAX_PHOTO_BYTES, type Photo } from '@pflanzen/shared';
import { api, getToken, path } from './api';
import { newId, refresh } from './store';

export interface Shrunk {
  blob: Blob;
  width: number;
  height: number;
}

/** Foto auf max. `maxSide` Pixel verkleinern und als JPEG ablegen. */
export async function shrinkImage(file: Blob, maxSide = 1600, quality = 0.82): Promise<Shrunk> {
  const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, maxSide / Math.max(bmp.width, bmp.height));
  const width = Math.round(bmp.width * scale);
  const height = Math.round(bmp.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, width, height);
  bmp.close();
  let q = quality;
  let blob = await toBlob(canvas, q);
  while (blob.size > MAX_PHOTO_BYTES * 0.9 && q > 0.4) {
    q -= 0.12;
    blob = await toBlob(canvas, q);
  }
  return { blob, width, height };
}

function toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Foto konnte nicht umgewandelt werden.'))), 'image/jpeg', quality);
  });
}

export async function uploadPhoto(plantId: string, file: Blob, opts: { eventId?: string | null; note?: string } = {}): Promise<Photo> {
  const { blob, width, height } = await shrinkImage(file);
  const meta = {
    id: newId('ph'),
    plantId,
    takenAt: new Date().toISOString(),
    width,
    height,
    eventId: opts.eventId ?? null,
    note: opts.note ?? '',
  };
  const form = new FormData();
  form.append('file', blob, `${meta.id}.jpg`);
  form.append('meta', JSON.stringify(meta));
  const photo = await api<Photo>('POST', path('photos'), form, { timeoutMs: 60_000 });
  await refresh();
  return photo;
}

const urlCache = new Map<string, Promise<string>>();

/** Foto mit Token laden und als lokale Bild-URL zurückgeben (wird gemerkt). */
export function photoUrl(photoId: string): Promise<string> {
  let p = urlCache.get(photoId);
  if (!p) {
    p = (async () => {
      const token = await getToken();
      const res = await fetch(path('photoFile', { id: photoId }), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!res.ok) throw new Error(`Foto ${res.status}`);
      return URL.createObjectURL(await res.blob());
    })();
    p.catch(() => urlCache.delete(photoId));
    urlCache.set(photoId, p);
  }
  return p;
}
